// Request details: breakdown, who/what, the pay link, history and actions.
import React, { useEffect, useRef, useState } from 'react';
import { Msr } from '../OireachtasPanel/symbols';
import { getRequest } from './api';
import useDialogFocus from './useDialogFocus';
import { ATTENTION_LABELS, EVENT_TYPE_LABELS, dueDay, errText, money, statusPill, when } from './paymentRequestModel';

const ATTEMPT_TEXT = {
  pending: 'Payment started',
  succeeded: 'Payment succeeded',
  canceled: 'Payment attempt abandoned',
};

function history(r) {
  const items = [];
  if (r.createdAt) items.push({ at: r.createdAt, icon: 'add_circle', text: `Created${r.createdBy?.name ? ` by ${r.createdBy.name}` : ''}` });
  (r.sends || []).forEach((s) => items.push({ at: s.at, icon: 'mail', text: `Emailed to ${s.to}` }));
  (r.attempts || []).forEach((a) =>
    items.push({
      at: a.paidAt || a.createdAt,
      icon: a.status === 'succeeded' ? 'check_circle' : a.status === 'canceled' ? 'remove_circle' : 'credit_card',
      text: `${ATTEMPT_TEXT[a.status] || a.status} · ${money(a.amountCents, r.currency)}`,
    })
  );
  (r.anomalies || []).forEach((a) =>
    items.push({ at: a.at, icon: 'warning', text: ATTENTION_LABELS[a.kind] || a.kind, warn: true })
  );
  if (r.receiptEmailSentAt) items.push({ at: r.receiptEmailSentAt, icon: 'receipt_long', text: `Receipt #${r.receipt} emailed` });
  if (r.canceledAt) items.push({ at: r.canceledAt, icon: 'block', text: 'Cancelled' });
  return items.sort((a, b) => new Date(a.at) - new Date(b.at));
}

export default function RequestDrawer({ id, version, onClose, onCopy, onResend, onCancel, onResendReceipt, notice }) {
  const ref = useRef(null);
  const [state, setState] = useState({ loading: true, error: '', request: null });
  useDialogFocus(ref, onClose, false);

  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true, error: '' }));
    getRequest(id)
      .then((res) => live && setState({ loading: false, error: '', request: res.request }))
      .catch((e) => live && setState({ loading: false, error: errText(e), request: null }));
    return () => {
      live = false;
    };
  }, [id, version]);

  const r = state.request;
  const pill = r ? statusPill(r) : null;

  return (
    <div className="modal-scrim pr-drawer-scrim" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="pr-drawer" role="dialog" aria-modal="true" aria-labelledby="pr-dr-title" ref={ref} tabIndex={-1}>
        <div className="pr-drawer-h">
          <div>
            <div className="op-title" id="pr-dr-title" role="heading" aria-level="2">
              {r ? `${money(r.totalCents, r.currency)} from ${r.payer?.name || 'payer'}` : 'Payment request'}
            </div>
            {r && (
              <div className="pr-drawer-sub">
                <span className={`op-pill ${pill.cls}`}>{pill.label}</span>
                <span className="op-muted op-small">
                  sent {when(r.createdAt)}
                  {r.dueAt ? ` · pay by ${dueDay(r.dueAt)}` : ''}
                </span>
              </div>
            )}
          </div>
          <button type="button" className="pr-x kebab-btn" aria-label="Close" onClick={onClose} data-autofocus>
            <Msr>close</Msr>
          </button>
        </div>

        <div className="pr-drawer-b">
          {state.loading && !r ? (
            <div className="op-note">Loading…</div>
          ) : state.error ? (
            <div className="op-error" role="alert">{state.error}</div>
          ) : r ? (
            <>
              <div>
                <div className="op-h3 pr-h3-first">For</div>
                <p className="op-p pr-flush">{r.note}</p>
              </div>
              <div className="pr-total pay-body">
                <div className="prow">
                  <span className="k">
                    To {r.organizer?.name}
                    <span className="subk">
                      {r.event?.name} · {EVENT_TYPE_LABELS[r.event?.type] || r.event?.type}
                      {r.accountName ? ` · Stripe: ${r.accountName}` : ''}
                    </span>
                  </span>
                  <span className="v">{money(r.organizerAmountCents, r.currency)}</span>
                </div>
                <div className="prow">
                  <span className="k">Platform fee</span>
                  <span className="v">{money(r.feeCents, r.currency)}</span>
                </div>
                <div className="prow total">
                  <span className="k">Total</span>
                  <span className="v">
                    {money(r.totalCents, r.currency)}
                    <span className="cur">{String(r.currency || '').toUpperCase()}</span>
                  </span>
                </div>
              </div>
              <dl className="op-dl">
                <dt>Payer</dt>
                <dd>
                  {r.payer?.name}
                  <br />
                  <span className="op-muted op-small">{r.payer?.email}</span>
                </dd>
                <dt>Organizer</dt>
                <dd>{r.organizer?.name}</dd>
                {r.createdBy?.name && (
                  <>
                    <dt>Created by</dt>
                    <dd>{r.createdBy.name}</dd>
                  </>
                )}
                {r.receipt && (
                  <>
                    <dt>Receipt</dt>
                    <dd>#{r.receipt}</dd>
                  </>
                )}
              </dl>
              {r.receiptEmailError && r.attention === 'receipt_not_sent' && (
                <div className="op-error">The receipt email didn’t send: {r.receiptEmailError}</div>
              )}
              {r.payUrl && (
                <div>
                  <div className="op-h3">Pay link</div>
                  <div className="pr-link">
                    <Msr>link</Msr>
                    <span>{r.payUrl}</span>
                  </div>
                </div>
              )}
              <div>
                <div className="op-h3">History</div>
                <ul className="pr-tl">
                  {history(r).map((h, i) => (
                    <li key={i} className={h.warn ? 'warn' : ''}>
                      <Msr>{h.icon}</Msr>
                      <div>
                        {h.text}
                        <span>{when(h.at)}</span>
                      </div>
                    </li>
                  ))}
                  {r.status === 'open' && !(r.attempts || []).length && (
                    <li>
                      <Msr>hourglass_empty</Msr>
                      <div>
                        Not paid yet<span>No payment attempts</span>
                      </div>
                    </li>
                  )}
                </ul>
              </div>
            </>
          ) : null}
        </div>

        {/* Feedback for actions started here, inside the dialog (the page
            behind is inert while the drawer is open). The live region is
            always present so a polite status is announced; a warning is an
            alert. */}
        <div className="pr-drawer-msg" aria-live="polite" aria-atomic="true">
          {notice && (
            <div className={`ds-notice${notice.warn ? ' pr-warn' : ''}`} role={notice.warn ? 'alert' : 'status'}>
              <Msr>{notice.warn ? 'error' : 'check_circle'}</Msr>
              {notice.text}
            </div>
          )}
        </div>

        {r && (
          <div className="pr-drawer-a">
            {r.status === 'open' && (
              <>
                <button type="button" className="op-btn" onClick={() => onCopy(r)}>
                  <Msr>link</Msr>Copy link
                </button>
                <button type="button" className="op-btn" onClick={() => onResend(r)}>
                  <Msr>forward_to_inbox</Msr>Resend
                </button>
                <button type="button" className="op-btn pr-push" onClick={() => onCancel(r)}>
                  <Msr>block</Msr>Cancel request
                </button>
              </>
            )}
            {r.attention === 'receipt_not_sent' && (
              <button type="button" className="op-btn" onClick={() => onResendReceipt(r)}>
                <Msr>receipt_long</Msr>Resend receipt
              </button>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
